import {
  Checkbox,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Loader2, Package, Search, X } from "lucide-react";
import React, { useEffect, useState } from "react";

const ItemListDrawerDeliveryOrder = ({
  selectedOrder,
  drawerOpen,
  handleCloseDrawer,
  onTransferItems,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
}) => {
  const [soItems, setSoItems] = useState([]);
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [checkedItems, setCheckedItems] = useState({});
  const [itemQuantities, setItemQuantities] = useState({});
  const [loadingItems, setLoadingItems] = useState(false);

  // Fetch Sales Order details when drawer opens or selectedOrder changes
  useEffect(() => {
    if (drawerOpen && selectedOrder?.soNo) {
      fetchSalesOrderItems(selectedOrder.soNo);
    }
  }, [drawerOpen, selectedOrder]);

  const fetchSalesOrderItems = async (soNo) => {
    setLoadingItems(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/DocumentEditDetails/${COMPANY_CODE}/SORDPEND/${soNo}/${DEPARTMENT}`,
      );
      if (!response.ok) throw new Error("Failed to fetch SO details");

      const data = await response.json();

      // Handle both array and single object responses
      const itemsData = Array.isArray(data) ? data : [data];
      setSoItems(itemsData);

      // Initialize quantities and checked items
      const initialQuantities = {};
      const initialChecked = {};
      itemsData.forEach((item, index) => {
        const itemKey = `so_${index}`;
        initialQuantities[itemKey] = Number(
          item.Ord_Pend_Qty || item.so_qty || 0,
        );
        initialChecked[itemKey] = false;
      });
      setItemQuantities(initialQuantities);
      setCheckedItems(initialChecked);
    } catch (error) {
      console.error("Error fetching SO items:", error);
      setSoItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleQuantityChange = (itemKey, newQty) => {
    const itemIndex = parseInt(itemKey.split("_")[1]);
    const pendingQty = Number(
      soItems[itemIndex].Ord_Pend_Qty || soItems[itemIndex].so_qty || 0,
    );

    // Only allow values > 0 and ≤ pendingQty
    if (newQty <= 0 || newQty > pendingQty) return;

    setItemQuantities((prev) => ({
      ...prev,
      [itemKey]: newQty,
    }));
  };

  const handleCheckboxToggle = (itemKey) => {
    setCheckedItems((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  const handleSelectAll = (selectAll) => {
    const newCheckedItems = {};

    soItems.forEach((item, index) => {
      const hasStock = Number(item.Current_Stk || 0) > 0;

      newCheckedItems[`so_${index}`] = hasStock ? selectAll : false;
    });

    setCheckedItems(newCheckedItems);
  };

  const filteredItems = soItems.filter((item) => {
    const searchLower = itemSearchTerm.toLowerCase();
    return (
      item.so_icode?.toLowerCase().includes(searchLower) ||
      item.idesc?.toLowerCase().includes(searchLower) ||
      item.Item_Description?.toLowerCase().includes(searchLower) ||
      false
    );
  });

  const getSelectedItems = () => {
    return soItems
      .map((item, index) => {
        const itemKey = `so_${index}`;
        if (checkedItems[itemKey]) {
          return {
            ...item,
            qty:
              itemQuantities[itemKey] ||
              Number(item.Ord_Pend_Qty || item.so_qty || 0),
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const handleTransfer = () => {
    const selectedItems = getSelectedItems();
    if (selectedItems.length > 0) {
      onTransferItems(soItems[0], selectedItems);
      handleCloseDrawer();
    }
  };

  const hasAnyChecked = Object.values(checkedItems).some((val) => val);
  const checkedCount = Object.values(checkedItems).filter((val) => val).length;
  const selectableKeys = soItems
    .map((item, index) =>
      Number(item.Current_Stk || 0) > 0 ? `so_${index}` : null,
    )
    .filter(Boolean);

  const isAllSelected = () =>
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => checkedItems[key]);

  const isSomeSelected = () =>
    selectableKeys.some((key) => checkedItems[key]) && !isAllSelected();

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={handleCloseDrawer}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 700 } },
      }}
    >
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-green-600 to-green-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">
                Sales Order Items
              </h3>
              <p className="text-sm text-green-100 mt-0.5">
                {selectedOrder?.soNo} • {selectedOrder?.customer}
              </p>
            </div>
            <IconButton
              onClick={handleCloseDrawer}
              sx={{
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <X className="w-5 h-5" />
            </IconButton>
          </div>
        </div>

        {/* Item Search Field */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
          <TextField
            fullWidth
            size="small"
            placeholder="Search items by code or description..."
            value={itemSearchTerm}
            onChange={(e) => setItemSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search className="w-4 h-4 text-gray-400" />
                </InputAdornment>
              ),
              endAdornment: itemSearchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setItemSearchTerm("")}
                    edge="end"
                  >
                    <X className="w-4 h-4" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <Checkbox
                size="small"
                checked={isAllSelected()}
                indeterminate={isSomeSelected()}
                onChange={(e) => handleSelectAll(e.target.checked)}
                sx={{ padding: "4px" }}
              />
              <span className="text-xs text-gray-700 font-medium">
                Select All
              </span>
            </div>
            <button
              onClick={handleTransfer}
              disabled={!hasAnyChecked}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                hasAnyChecked
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-green-200 cursor-not-allowed"
              }`}
              title={
                hasAnyChecked
                  ? `${checkedCount} items selected`
                  : "No items selected"
              }
            >
              Create Delivery Order ({checkedCount})
            </button>
          </div>
        </div>

        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loadingItems ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-green-600 mr-2" />
              <span className="text-sm text-gray-600">
                Loading sales order items...
              </span>
            </div>
          ) : filteredItems.length > 0 ? (
            <div className="space-y-3">
              {filteredItems.map((item, index) => {
                const originalIndex = soItems.indexOf(item);
                const itemKey = `so_${originalIndex}`;

                // Quantities from backend
                const soQty = Number(item.so_qty || 0);
                const deliveredQty = Number(item.si_qty || 0);
                const pendingQty = Number(item.Ord_Pend_Qty || 0);
                const woIssuedQty = Number(item.BWHrs || 0);
                const currentStock = Number(item.Current_Stk || 0);

                // State
                const isChecked = checkedItems[itemKey] || false;

                const currentQty =
                  itemQuantities[itemKey] !== undefined
                    ? itemQuantities[itemKey]
                    : Math.min(pendingQty, currentStock);

                const isInputDisabled = !isChecked || currentStock <= 0;

                return (
                  <div
                    key={itemKey}
                    className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${
                      isChecked
                        ? "border-green-300 bg-green-50/30"
                        : "border-gray-200"
                    }`}
                  >
                    {/* Item Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <Checkbox
                        checked={isChecked}
                        disabled={currentStock <= 0}
                        onChange={() => handleCheckboxToggle(itemKey)}
                        sx={{ padding: "4px", marginTop: "-4px" }}
                      />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div>
                            <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded">
                              {item.so_icode?.trim() || "-"}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                              Line #{index + 1}
                            </span>
                          </div>

                          {currentStock <= 0 && (
                            <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded">
                              No Stock
                            </span>
                          )}
                        </div>

                        <h4 className="text-sm font-medium text-gray-900 line-clamp-2">
                          {item.Item_Description || item.idesc || "-"}
                        </h4>
                      </div>
                    </div>

                    {/* Item Details Grid */}
                    <div className="grid grid-cols-6 gap-3">
                      {/* SO Qty */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1.5">SO Qty</p>
                        <p className="text-sm font-semibold text-blue-600">
                          {soQty.toFixed(2)}
                        </p>
                      </div>

                      {/* Delivered Qty */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1.5">
                          Delivered Qty
                        </p>
                        <p className="text-sm font-semibold text-indigo-600">
                          {deliveredQty.toFixed(2)}
                        </p>
                      </div>

                      {/* WO Issued */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1.5">
                          WO Issued
                        </p>
                        <p className="text-sm font-semibold text-green-600">
                          {woIssuedQty.toFixed(2)}
                        </p>
                      </div>

                      {/* Pending Qty */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1.5">
                          Pending Qty
                        </p>
                        <p className="text-sm font-semibold text-purple-600">
                          {pendingQty.toFixed(2)}
                        </p>
                      </div>

                      {/* Current Stock */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1.5">
                          Current Stock
                        </p>
                        <p
                          className={`text-sm font-semibold ${
                            currentStock === 0
                              ? "text-red-600"
                              : currentStock < pendingQty
                                ? "text-amber-600"
                                : "text-green-600"
                          }`}
                        >
                          {currentStock.toFixed(2)}
                        </p>
                      </div>

                      {/* DO Qty Input */}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1.5">
                          DO Qty
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={Math.min(pendingQty, currentStock)}
                          value={currentQty}
                          disabled={isInputDisabled}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            if (value > pendingQty) return;
                            if (value > currentStock) return;
                            handleQuantityChange(itemKey, value);
                          }}
                          onFocus={(e) => e.target.select()}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md
                             focus:ring-2 focus:ring-green-500 text-sm
                             disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : itemSearchTerm ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Search className="w-12 h-12 mb-3 opacity-40" />
              <p className="text-sm">No items match your search</p>
              <p className="text-xs text-gray-400 mt-1">
                Try different keywords
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Package className="w-12 h-12 mb-3 opacity-40" />
              <p className="text-sm">No items found</p>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default ItemListDrawerDeliveryOrder;
